import { RouterProvider, RouterProviderProps } from "react-router-dom";
import { defaultTheme } from "defaultTheme.ts";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { createContext } from "react";
import { FeatureFlagsProvider } from "context/FeatureFlagsContext.tsx";
import * as Sentry from "@sentry/react";

const mode = import.meta.env.MODE; // "dev" | "sdbx-ampeco" | "stg-ampeco" | "prod-ampeco" | ...

if (mode !== "dev") {
    const isProduction = mode.startsWith("prod");
    Sentry.init({
        dsn: "https://e2ba281751c2471595f1d3209c5f4faa@o4508041774825472.ingest.de.sentry.io/4508052520042576",
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
        ],
        environment: mode, // correctly reports "sdbx-ampeco", "stg-ampeco", "prod-ampeco", etc.
        sampleRate: 1.0,

        // Tracing
        tracesSampleRate: 1.0, // Capture 100% of the transactions
        tracePropagationTargets: [/\.oreve\.com$/],

        // Session Replay
        replaysSessionSampleRate: 0, // 0% sessions recorded
        replaysOnErrorSampleRate: isProduction ? 1.0 : 0, // 100% replay on error in prod only
    });
}

interface AppProps {
    router: RouterProviderProps["router"];
}

export const FeatureToggle = createContext<boolean>(true);

export const App = ({ router }: AppProps) => {
    return (
        <ThemeProvider theme={defaultTheme}>
            <FeatureToggle.Provider value={(import.meta.env.VITE_FEATURE_TOGGLE ?? "true") === "true"}>
                <FeatureFlagsProvider>
                    <CssBaseline />
                    <RouterProvider router={router} />
                </FeatureFlagsProvider>
            </FeatureToggle.Provider>
        </ThemeProvider>
    );
};